import React, { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { useGroupDebtorCandidates, useStudentPermissions } from 'core/hooks';
import { LmsBlockModal } from 'molecules';

import { GroupColors } from './groupColors';

interface IGroupDebtorsPanelProps {
  groupId: string;
}

/**
 * Group-level debtor candidates (spec 0003): students with an issued invoice
 * overdue > threshold, with a per-row "block / unblock LMS access" action. The
 * candidate set is corrections / transfer-aware (computed server-side); blocking
 * is the account manager's manual confirmation, restore is automatic once paid.
 */
export const GroupDebtorsPanel = memo(
  ({ groupId }: IGroupDebtorsPanelProps) => {
    const { t } = useTranslation();
    const { canBlockLmsAccess } = useStudentPermissions();
    const {
      candidates,
      loading,
      mutating,
      block,
      unblock,
    } = useGroupDebtorCandidates(groupId);

    const [target, setTarget] = useState<{
      studentId: string;
      mode: 'block' | 'unblock';
    } | null>(null);

    const onToggle = useCallback((studentId: string, isBlocked: boolean) => {
      setTarget({ studentId, mode: isBlocked ? 'unblock' : 'block' });
    }, []);

    const onSubmit = useCallback(
      async (reason?: string) => {
        if (!target) return;
        const ok =
          target.mode === 'unblock'
            ? await unblock(target.studentId)
            : await block(target.studentId, reason || undefined);
        if (ok) setTarget(null);
      },
      [target, block, unblock],
    );

    if (loading && candidates.length === 0) {
      return null;
    }

    return (
      <Card>
        <CardTitle>{t('pages.groupDetail.debtors.title')}</CardTitle>
        {candidates.length === 0 ? (
          <Muted>{t('pages.groupDetail.debtors.none')}</Muted>
        ) : (
          <Table>
            <HeadRow>
              <span>{t('pages.groupDetail.debtors.student')}</span>
              <Num>{t('pages.groupDetail.debtors.overdue')}</Num>
              <Num>{t('pages.groupDetail.debtors.invoices')}</Num>
              <Num>{t('pages.groupDetail.debtors.days')}</Num>
              <span />
            </HeadRow>
            {candidates.map((c) => (
              <BodyRow key={c.studentId}>
                <Name title={c.email}>
                  {c.fullName}
                  {c.currentlyBlocked ? (
                    <Badge>{t('pages.groupDetail.debtors.blocked')}</Badge>
                  ) : null}
                </Name>
                <Num>{c.overdueAmount.toFixed(2)} PLN</Num>
                <Num>{c.overdueInvoiceCount}</Num>
                <Num>{c.maxOverdueDays}</Num>
                <span>
                  {canBlockLmsAccess ? (
                    <ActionButton
                      type="button"
                      blocked={c.currentlyBlocked}
                      disabled={mutating}
                      onClick={() => onToggle(c.studentId, c.currentlyBlocked)}
                    >
                      {c.currentlyBlocked
                        ? t('pages.studentDetail.lmsBlock.unblock')
                        : t('pages.studentDetail.lmsBlock.block')}
                    </ActionButton>
                  ) : null}
                </span>
              </BodyRow>
            ))}
          </Table>
        )}
        {target ? (
          <LmsBlockModal
            key={`${target.studentId}:${target.mode}`}
            mode={target.mode}
            loading={mutating}
            onSubmit={onSubmit}
            onCancel={() => setTarget(null)}
          />
        ) : null}
      </Card>
    );
  },
);

const Card = styled.div`
  margin-top: 16px;
  padding: 14px 16px;
  border: 1px solid ${GroupColors.border};
  border-radius: 10px;
  background: ${GroupColors.surface};
`;

const CardTitle = styled.div`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${GroupColors.textMuted};
  margin-bottom: 10px;
`;

const Muted = styled.div`
  font-size: 13px;
  color: ${GroupColors.textMuted};
`;

const Table = styled.div`
  display: flex;
  flex-direction: column;
`;

const rowGrid = `
  display: grid;
  grid-template-columns: 1fr 120px 90px 90px 140px;
  align-items: center;
  gap: 8px;
`;

const HeadRow = styled.div`
  ${rowGrid}
  padding: 6px 4px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${GroupColors.textMuted};
  border-bottom: 1px solid ${GroupColors.border};
`;

const BodyRow = styled.div`
  ${rowGrid}
  padding: 8px 4px;
  font-size: 13px;
  color: ${GroupColors.textSecondary};
  border-bottom: 1px solid ${GroupColors.border};
`;

const Name = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
`;

const Num = styled.span`
  text-align: right;
  font-variant-numeric: tabular-nums;
`;

const Badge = styled.span`
  font-size: 11px;
  font-weight: 700;
  color: ${GroupColors.surface};
  background: ${GroupColors.danger};
  border-radius: 999px;
  padding: 1px 8px;
`;

const ActionButton = styled.button<{ blocked: boolean }>`
  border: 1px solid
    ${({ blocked }) => (blocked ? GroupColors.accent : GroupColors.danger)};
  background: ${GroupColors.surface};
  color: ${({ blocked }) =>
    blocked ? GroupColors.accent : GroupColors.danger};
  padding: 5px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;
